import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { STORAGE_KEYS } from '@constants';

interface GameSettings {
  soundEnabled: boolean;
  musicEnabled: boolean;
  vibrationEnabled: boolean;
  graphicsQuality: 'low' | 'medium' | 'high';
  language: string;
}

interface GameProgress {
  currentLevel: number;
  highScore: number;
  totalScore: number;
  achievements: string[];
  unlockedItems: string[];
}

interface GameState {
  settings: GameSettings;
  progress: GameProgress;
  isPlaying: boolean;
  isPaused: boolean;
  currentScore: number;
  
  updateSettings: (settings: Partial<GameSettings>) => void;
  updateProgress: (progress: Partial<GameProgress>) => void;
  startGame: () => void;
  pauseGame: () => void;
  resumeGame: () => void;
  endGame: (score: number) => void;
  addScore: (points: number) => void;
  unlockAchievement: (achievementId: string) => void;
  unlockItem: (itemId: string) => void;
}

const defaultSettings: GameSettings = {
  soundEnabled: true,
  musicEnabled: true,
  vibrationEnabled: true,
  graphicsQuality: 'medium',
  language: 'en',
};

const defaultProgress: GameProgress = {
  currentLevel: 1,
  highScore: 0,
  totalScore: 0,
  achievements: [],
  unlockedItems: [],
};

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      settings: defaultSettings,
      progress: defaultProgress,
      isPlaying: false,
      isPaused: false,
      currentScore: 0,

      updateSettings: (newSettings) => {
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        }));
      },

      updateProgress: (newProgress) => {
        set((state) => ({
          progress: { ...state.progress, ...newProgress },
        }));
      },

      startGame: () => {
        set({
          isPlaying: true,
          isPaused: false,
          currentScore: 0,
        });
      },

      pauseGame: () => {
        set({ isPaused: true });
      },

      resumeGame: () => {
        set({ isPaused: false });
      },

      endGame: (score) => {
        const { progress } = get();
        const newHighScore = Math.max(progress.highScore, score);
        const newTotalScore = progress.totalScore + score;
        
        set({
          isPlaying: false,
          isPaused: false,
          progress: {
            ...progress,
            highScore: newHighScore,
            totalScore: newTotalScore,
          },
        });
      },

      addScore: (points) => {
        set((state) => ({
          currentScore: state.currentScore + points,
        }));
      },

      unlockAchievement: (achievementId) => {
        const { progress } = get();
        if (!progress.achievements.includes(achievementId)) {
          set({
            progress: {
              ...progress,
              achievements: [...progress.achievements, achievementId],
            },
          });
        }
      },

      unlockItem: (itemId) => {
        const { progress } = get();
        if (!progress.unlockedItems.includes(itemId)) {
          set({
            progress: {
              ...progress,
              unlockedItems: [...progress.unlockedItems, itemId],
            },
          });
        }
      },
    }),
    {
      name: STORAGE_KEYS.GAME_PROGRESS,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        settings: state.settings,
        progress: state.progress,
      }),
    }
  )
);